/**
 * Property-level obituary outcomes. A technical failure is not a no-match.
 * Only a completed, evidence-valid search may produce `no_match`.
 */

export const OBITUARY_CONFIRMED_MATCH_STATUSES = ["confirmed_match", "operator_accepted"] as const;
export const OBITUARY_VALID_NO_MATCH_STATUS = "no_match" as const;
export const OBITUARY_AMBIGUOUS_STATUS = "ambiguous_review" as const;
export const OBITUARY_INSUFFICIENT_EVIDENCE_STATUS = "insufficient_evidence" as const;
export const OBITUARY_PROVIDER_FAILURE_STATUS = "provider_failure" as const;
export const OBITUARY_RETRYABLE_ERROR_STATUS = "retryable_error" as const;

export type ObituaryPropertyMatchStatus =
  | (typeof OBITUARY_CONFIRMED_MATCH_STATUSES)[number]
  | typeof OBITUARY_VALID_NO_MATCH_STATUS
  | typeof OBITUARY_AMBIGUOUS_STATUS
  | typeof OBITUARY_INSUFFICIENT_EVIDENCE_STATUS
  | typeof OBITUARY_PROVIDER_FAILURE_STATUS
  | typeof OBITUARY_RETRYABLE_ERROR_STATUS
  | "queued";

export type ObituaryOutcomeKind =
  | "confirmed_identity"
  | "valid_no_match"
  | "ambiguous_review"
  | "insufficient_evidence"
  | "provider_failure"
  | "retryable_error"
  | "open";

const CONFIRMED = new Set<string>(OBITUARY_CONFIRMED_MATCH_STATUSES);
const VALID_NO_MATCH = new Set(["no_match", "obituary_no_match"]);
const AMBIGUOUS = new Set(["ambiguous_review", "obituary_ambiguous_match", "obituary_identity_mismatch"]);

const INSUFFICIENT_EVIDENCE = new Set([
  "insufficient_evidence",
  "obituary_incomplete_evidence",
  "obituary_parsing_failed",
  "obituary_provider_coverage_gap",
  "obituary_text_missing",
  "obituary_text_too_large",
  "obituary_artifact_failed",
  "obituary_source_not_authorized",
  "obituary_source_paywalled",
  "obituary_input_invalid",
  "obituary_owner_unparseable",
  "obituary_purged"
]);

const PROVIDER_FAILURE = new Set([
  "provider_failure",
  "obituary_provider_blocked",
  "obituary_provider_authentication_failed",
  "obituary_provider_payment_required",
  "obituary_provider_rate_limited",
  "obituary_provider_changed",
  "obituary_research_disabled",
  "obituary_not_configured",
  "obituary_browser_worker_not_configured",
  "obituary_google_search_unavailable",
  "obituary_brightdata_not_configured"
]);

const RETRYABLE_ERROR = new Set([
  "retryable_error",
  "obituary_timeout",
  "obituary_unexpected"
]);

const TRANSIENT_PROVIDER_FAILURE = new Set([
  "obituary_provider_payment_required",
  "obituary_provider_rate_limited",
  "obituary_research_disabled",
  "obituary_not_configured",
  "obituary_browser_worker_not_configured",
  "obituary_google_search_unavailable",
  "obituary_brightdata_not_configured"
]);

function normalize(value: unknown): string {
  return value == null ? "" : String(value).replaceAll(/\s+/g, " ").trim().toLowerCase();
}

function classifyOne(value: unknown): ObituaryOutcomeKind {
  const text = normalize(value);
  if (!text) return "open";
  if (CONFIRMED.has(text)) return "confirmed_identity";
  if (VALID_NO_MATCH.has(text)) return "valid_no_match";
  if (AMBIGUOUS.has(text)) return "ambiguous_review";
  if (INSUFFICIENT_EVIDENCE.has(text)) return "insufficient_evidence";
  if (PROVIDER_FAILURE.has(text)) return "provider_failure";
  if (RETRYABLE_ERROR.has(text)) return "retryable_error";
  return "open";
}

export function classifyObituaryOutcome(matchStatus?: unknown, failureClass?: unknown): ObituaryOutcomeKind {
  const failureKind = classifyOne(failureClass);
  if (failureKind !== "open") return failureKind;
  return classifyOne(matchStatus);
}

export function isObituaryTransientProviderFailure(value: unknown): boolean {
  return TRANSIENT_PROVIDER_FAILURE.has(normalize(value));
}

export function obituaryPropertyMatchForKind(kind: ObituaryOutcomeKind, matchStatus?: unknown): string | null {
  switch (kind) {
    case "confirmed_identity": {
      const match = normalize(matchStatus);
      return CONFIRMED.has(match) ? match : "confirmed_match";
    }
    case "valid_no_match":
      return OBITUARY_VALID_NO_MATCH_STATUS;
    case "ambiguous_review":
      return OBITUARY_AMBIGUOUS_STATUS;
    case "insufficient_evidence":
      return OBITUARY_INSUFFICIENT_EVIDENCE_STATUS;
    case "provider_failure":
      return OBITUARY_PROVIDER_FAILURE_STATUS;
    case "retryable_error":
      return OBITUARY_RETRYABLE_ERROR_STATUS;
    case "open":
      return null;
  }
}

/**
 * Stampable house match for a finished run. Retryable and wait-class provider
 * failures stay unstamped so existing recovery can reclaim the paid job.
 * Never returns `no_match` for a technical failure.
 */
export function stampableObituaryPropertyMatch(input: {
  matchStatus?: string | null;
  failureClass?: string | null;
  stampRetryable?: boolean;
}): string | null {
  const kind = classifyObituaryOutcome(input.matchStatus, input.failureClass);
  if (kind === "open") return null;
  if (kind === "retryable_error" && input.stampRetryable !== true) return null;
  if (
    kind === "provider_failure"
    && input.stampRetryable !== true
    && isObituaryTransientProviderFailure(input.failureClass ?? input.matchStatus)
  ) {
    return null;
  }
  return obituaryPropertyMatchForKind(kind, input.matchStatus);
}

export function obituaryPlannerFailureClass(kind: ObituaryOutcomeKind): string | null {
  switch (kind) {
    case "valid_no_match":
      return "obituary_no_match";
    case "ambiguous_review":
      return "obituary_ambiguous_review";
    case "insufficient_evidence":
      return "obituary_insufficient_evidence";
    case "provider_failure":
      return "obituary_provider_failure";
    case "retryable_error":
      return "obituary_retryable_error";
    default:
      return null;
  }
}
